import { useQuery } from '@apollo/client';
import papaparse from 'papaparse';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex } from 'rebass';
import { useHistory } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import PageBar from '../../components/PageBar/PageBar';
import TableWindow from '../../components/Table/TableWindow';
import Button from '../../components/Button/Button';
import { useOrderBy } from '../../hooks/useOrderBy';
import type { Column, IdArrayInput, ItemLoader } from '../../hooks/useTablePages';
import useColumnConfiguration from '../../hooks/useColumnConfiguration';
import useStoredFilters from '../../hooks/useStoredFilters';
import useMode from '../../hooks/useMode';
import getColumns from './Activity.columns';
import {
  GET_ACTIVITY_ITEMS,
  type ActivityListItem,
} from '../../components/ActivityFeed/ActivityFeed.queries';
import FilterBarActivity, {
  ActivityFilters,
  defaultFilters,
} from './FilterBarActivity';
import { getNameLikeCondition } from '../../helpers/search';
import useNotificationActions from '../../components/ActivityFeed/useNotificationActions';
import ActivityActions from './ActivityActions';
import { ACTIVITY_ITEMS_UPDATED_EVENT } from '../../components/ActivityFeed/ActivityPollingToast';
import { downloadFile } from '../../helpers/download';

const convertStoredDate = (
  dateStart: string | undefined,
  dateEnd: string | undefined
) => ({
  eventTime:
    dateStart && dateEnd
      ? {
          between: [dateStart, dateEnd],
        }
      : dateStart || dateEnd
        ? {
            gte: dateStart ? dateStart : undefined,
            lte: dateEnd ? `${dateEnd}T23:59:59.999Z` : undefined,
          }
        : undefined,
});

export const convertFilters = (storedFilters: ActivityFilters) => {
  const where: Record<string, any> = {};
  const readFilter = storedFilters.readFilter ?? 'ALL';

  if (storedFilters.stringSearch && storedFilters.stringSearch.length > 1) {
    where.or = [
      {
        title: getNameLikeCondition(storedFilters.stringSearch),
      },
      {
        body: getNameLikeCondition(storedFilters.stringSearch),
      },
    ];
  }

  if (storedFilters.dateStart || storedFilters.dateEnd) {
    Object.assign(
      where,
      convertStoredDate(storedFilters.dateStart, storedFilters.dateEnd)
    );
  }

  if (readFilter === 'DISMISSED') {
    where.dismissed = { not: null };
  }

  return {
    where,
    unreadOnly: readFilter === 'UNREAD',
    includeDismissed: readFilter === 'DISMISSED',
  };
};

const downloadActivityItems = (
  items: ActivityListItem[],
  columns: Column[],
  filename: string
) => {
  const csv = papaparse.unparse({
    fields: columns.map((column) => column.header),
    data: items.map((item) =>
      columns.map((column) =>
        (column.stringAccessor || (column.accessor as (row: any) => string))(
          item
        )
      )
    ),
  });

  downloadFile(filename, csv);
};

const createActivityItemLoader = (
  items: ActivityListItem[]
): ItemLoader<ActivityListItem> => {
  const itemsById = new Map(items.map((item) => [item.id, item]));

  return {
    queryName: 'activityItems',
    useItem: (index: number) => items[index],
    get: (index: number) => items[index],
    getById: (itemId: string | number) => itemsById.get(String(itemId))!,
    has: (index: number) => index in items,
    load: async (index: number) => items[index],
    loadIds: async (itemIds: IdArrayInput<string | number>) =>
      itemIds
        .map((itemId) => itemsById.get(String(itemId)))
        .filter((item): item is ActivityListItem => !!item),
    loadAll: async () => items,
    downloadIds: async (
      itemIds: IdArrayInput<string | number>,
      columns: Column[],
      filename: string
    ) => {
      downloadActivityItems(
        itemIds
          .map((itemId) => itemsById.get(String(itemId)))
          .filter((item): item is ActivityListItem => !!item),
        columns,
        filename
      );
    },
    downloadAll: async (columns: Column[], filename: string) => {
      downloadActivityItems(items, columns, filename);
    },
  };
};

const Activity = (): JSX.Element => {
  const history = useHistory();
  const mode = useMode();
  const notificationActions = useNotificationActions();
  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<ActivityFilters>(
      convertFilters,
      'activity',
      defaultFilters
    );

  const baseColumns = useMemo(() => getColumns(notificationActions), [
    notificationActions,
  ]);
  const { orderBy, onHeaderClick } = useOrderBy(
    'activity',
    baseColumns,
    'eventTime',
    'DESC'
  );
  const tableOrderBy = useMemo(
    () => [{ col: 'followUp', dir: 'DESC' as const }, ...orderBy],
    [orderBy]
  );

  const activityItemsQuery = useQuery(GET_ACTIVITY_ITEMS, {
    variables: {
      limit: null,
      ...queryFilters,
      orderBy: tableOrderBy,
    },
    notifyOnNetworkStatusChange: true,
  });
  const items: ActivityListItem[] = activityItemsQuery.data?.activityItems ?? [];
  const itemIds = useMemo(() => items.map((item) => item.id), [items]);
  const itemLoader = useMemo(() => createActivityItemLoader(items), [items]);
  const [selectedIds, setSelectedIds] = useState<IdArrayInput<string>>([]);
  const refetch = activityItemsQuery.refetch;
  const loading = activityItemsQuery.loading;

  useEffect(() => {
    setSelectedIds((currentSelectedIds) =>
      currentSelectedIds.filter((selectedId) => itemIds.includes(selectedId))
    );
  }, [itemIds]);
  const handleActionComplete = useCallback(async () => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const handleActivityItemsUpdated = () => {
      refetch();
    };

    window.addEventListener(
      ACTIVITY_ITEMS_UPDATED_EVENT,
      handleActivityItemsUpdated
    );
    return () => {
      window.removeEventListener(
        ACTIVITY_ITEMS_UPDATED_EVENT,
        handleActivityItemsUpdated
      );
    };
  }, [refetch]);
  const availableColumns = useMemo(
    () => getColumns(notificationActions, handleActionComplete),
    [handleActionComplete, notificationActions]
  );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'activity');

  const selectedItems = useMemo(
    () => selectedIds.map((id) => itemLoader.getById(id)).filter(Boolean),
    [itemLoader, selectedIds]
  );
  const selectedHasUnread = selectedItems.some((item) => !item.read);
  const selectedHasFollowUp = selectedItems.some((item) => item.followUp);
  const selectedHasDismissed = selectedItems.some((item) => item.dismissed);

  const handleRowClick = useCallback(
    async (item: ActivityListItem) => {
      if (!item.actionPath) {
        return;
      }

      if (!item.read) {
        await notificationActions.markRead(item.id);
      }

      history.push(item.actionPath);
    },
    [history, notificationActions]
  );

  return (
    <Layout>
      <Flex
        flexGrow={1}
        flexDirection="column"
        px="mainContent"
        pb="mainContent"
      >
        <PageBar title="Activity">
          <Button
            variant="outline"
            icon="settings"
            title="Notification settings"
            aria-label="Notification settings"
            onClick={() => history.push(`/${mode}/notification-settings`)}
          />
        </PageBar>
        <FilterBarActivity
          filters={storedFilters}
          setFilters={setStoredFilters}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            columns={visibleColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            actionItems={
              <ActivityActions
                selectedIds={selectedIds}
                selectedHasUnread={selectedHasUnread}
                selectedHasFollowUp={selectedHasFollowUp}
                selectedHasDismissed={selectedHasDismissed}
                onActionComplete={handleActionComplete}
              />
            }
            onRowClick={handleRowClick}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            rowHeight={46}
            fillWidth
          />
        )}
      </Flex>
    </Layout>
  );
};

export default Activity;
